import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, ArrowRight, Check } from "lucide-react";
import { categoryName } from "@/data/simulations";
import { track } from "@/lib/analytics";

const ACCENT = "#4FA3D9";

// Reusable Preview drawer for AVAILABLE simulations (development builds may also open it for
// coming-soon simulations; the library decides). URL represents the open state
// (rendered when route = /simulations/:slug/preview).
export default function SimulationPreview({ sim, onClose, onPractice }) {
  const comingSoon = sim && sim.status !== "available";
  const panelRef = useRef(null);
  const firedRef = useRef(null);

  // Fire preview_open once per opened slug (dedupes StrictMode; ready for future prev/next).
  useEffect(() => {
    if (sim && firedRef.current !== sim.slug) {
      firedRef.current = sim.slug;
      track("simulation_preview_open", sim.slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim?.slug]);

  // Escape closes; focus the panel for accessibility.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => panelRef.current?.focus(), 60);
    return () => { window.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [onClose]);

  const handlePractice = () => {
    if (sim) track("simulation_preview_practice", sim.slug);
    onPractice(sim);
  };

  return (
    <AnimatePresence>
      {sim && (
        <div className="fixed inset-0 z-[60]" data-testid="simulation-preview" role="dialog" aria-modal="true" aria-label={`${sim.title} preview`}>
          {/* Backdrop — library stays visible behind */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-[3px]"
            data-testid="preview-backdrop"
          />

          {/* Panel: right drawer on desktop, bottom sheet on mobile */}
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            initial={{ x: "100%", y: 0, opacity: 1 }}
            animate={{ x: 0, y: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 32 }}
            className="absolute right-0 top-0 flex h-[100dvh] w-full max-w-md flex-col overflow-y-auto bg-[#0c0c0c] shadow-[0_0_80px_rgba(0,0,0,0.7)] outline-none sm:border-l sm:border-white/10"
          >
            {/* Close */}
            <button
              onClick={onClose}
              data-testid="preview-close-btn"
              aria-label="Close preview"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white/80 backdrop-blur-md transition-colors hover:bg-black/60"
            >
              <X size={18} />
            </button>

            {/* Visual glimpse */}
            <div className="relative h-52 w-full shrink-0 overflow-hidden">
              {sim.previewImage ? (
                <img src={sim.previewImage} alt={`${sim.title} — Adaptience simulation preview`} loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full" style={{ background: "linear-gradient(135deg, rgba(79,163,217,0.18), rgba(79,163,217,0.02))" }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/40 to-transparent" />
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col px-6 pb-8 sm:px-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
                {categoryName(sim.category)} · {sim.ageRange}
              </p>
              <h2 className="mt-2 font-display text-3xl font-semibold leading-[1.05] tracking-tight text-white" data-testid="preview-title">
                {sim.previewTitle || sim.title}
              </h2>
              {sim.previewLead && (
                <p className="mt-3 text-sm leading-relaxed text-white/60" data-testid="preview-lead">{sim.previewLead}</p>
              )}
              <span className="mt-3 inline-flex w-fit items-center gap-1.5 text-xs text-white/45">
                <Clock size={13} />
                {sim.duration}
              </span>

              <Section title="The Situation">
                <p className="text-sm leading-relaxed text-white/65">{sim.previewSituation}</p>
              </Section>

              {sim.capabilities?.length > 0 && (
                <Section title="What You'll Practise">
                  <ul className="space-y-2">
                    {sim.capabilities.map((c) => (
                      <li key={c} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/65">
                        <Check size={15} className="mt-0.5 shrink-0" style={{ color: ACCENT }} />
                        {c}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <Section title="What to Expect">
                <p className="text-sm leading-relaxed text-white/65">{sim.previewExpect}</p>
              </Section>

              {/* Actions */}
              <div className="mt-auto flex items-center gap-4 pt-8">
                <button
                  onClick={handlePractice}
                  data-testid="preview-practice-btn"
                  className="group inline-flex flex-1 items-center justify-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]"
                  style={{ background: ACCENT }}
                >
                  {comingSoon ? "Practice · Preview build" : "Practice"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={onClose}
                  data-testid="preview-close-text-btn"
                  className="rounded-full px-4 py-3.5 text-sm text-white/55 transition-colors hover:text-white"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-7 border-t border-white/[0.07] pt-6">
    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>{title}</p>
    {children}
  </div>
);
